import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  FiX,
  FiActivity,
  FiTrendingUp,
  FiTarget,
  FiInfo,
  FiMaximize2,
  FiMinimize2,
  FiStar,
  FiMoon,
  FiZap,
  FiChevronDown,
} from "react-icons/fi";
import { FaDumbbell } from "react-icons/fa";
import { Avaliation } from "../../types";
import {
  calculateSomatorioPerimetros,
  getPeitoralFold,
} from "../../helpers/avaliationCalculations";
import Tooltip from "../ui/Tooltip";
import ImageViewerModal from "../ui/ImageViewerModal";

interface BodyAvaliationModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentName: string;
  professionalName: string;
  data: Avaliation | null;
  calculated: {
    percentualGordura?: number | null;
    imc?: number | null;
    pesoMagro?: number | null;
    pesoGordo?: number | null;
    distribuicaoMMSS?: number | null;
    distribuicaoMMII?: number | null;
    distribuicaoTronco?: number | null;
    somatorioPerimetros?: number | null;
  };
  date?: string | null;
  avaliations?: Avaliation[];
  onSelectAvaliation?: (avaliation: Avaliation) => void;
}

// Componentes auxiliares

function StatCard({
  icon: Icon,
  label,
  value,
  subtitle,
  color,
  tooltip,
}: Readonly<{
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: string;
  subtitle: string;
  color: string;
  tooltip?: string;
}>) {
  const colorClasses = {
    purple:
      "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400",
    blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400",
    green:
      "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400",
    orange:
      "bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400",
  };

  return (
    <div className="rounded-2xl border border-stroke bg-white p-3 sm:p-5 shadow-sm dark:border-strokedark dark:bg-boxdark">
      <div className="flex items-start gap-2 sm:gap-3">
        <div
          className={`rounded-xl p-2 sm:p-3 flex-shrink-0 ${colorClasses[color as keyof typeof colorClasses]}`}
        >
          <Icon size={20} className="sm:w-6 sm:h-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1 truncate">
            {label}
            {tooltip && (
              <Tooltip content={tooltip}>
                <FiInfo
                  size={12}
                  className="cursor-help text-gray-400 hover:text-gray-600 flex-shrink-0"
                />
              </Tooltip>
            )}
          </p>
          <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
            {value}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {subtitle}
          </p>
        </div>
      </div>
    </div>
  );
}

function InfoCardMobile({
  label,
  value,
}: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg bg-white/50 px-2 py-3 dark:bg-gray-800/50">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
        {label}
      </span>
      <span className="text-sm font-bold text-gray-900 dark:text-white text-center">
        {value}
      </span>
    </div>
  );
}

function CollapsibleSection({
  title,
  isExpanded,
  onToggle,
  color,
  tooltip,
  children,
}: Readonly<{
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  color: string;
  tooltip?: string;
  children: React.ReactNode;
}>) {
  const colorClasses = {
    purple: "bg-purple-600",
    blue: "bg-blue-600",
    green: "bg-green-600",
    red: "bg-red-600",
  };

  return (
    <div className="lg:hidden rounded-2xl border border-stroke bg-white dark:border-strokedark dark:bg-boxdark shadow-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 touch-manipulation active:bg-gray-100 dark:active:bg-gray-700"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div
            className={`h-1 w-8 rounded flex-shrink-0 ${colorClasses[color as keyof typeof colorClasses]}`}
          ></div>
          <h4 className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300 truncate">
            {title}
          </h4>
          {tooltip && (
            <Tooltip content={tooltip}>
              <FiInfo size={14} className="text-gray-400 flex-shrink-0" />
            </Tooltip>
          )}
        </div>
        <FiChevronDown
          className={`flex-shrink-0 text-gray-400 transition-transform duration-200 ${
            isExpanded ? "rotate-180" : ""
          }`}
          size={20}
        />
      </button>
      {isExpanded && (
        <div className="p-4 pt-0 border-t border-gray-100 dark:border-gray-700">
          {children}
        </div>
      )}
    </div>
  );
}

function MeasureRow({
  label,
  value,
}: Readonly<{
  label: string;
  value: string | number | null | undefined;
}>) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 py-2 last:border-0 dark:border-gray-700">
      <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
        {label}
      </span>
      <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
        {value || "-"}
      </span>
    </div>
  );
}

function InfoRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-white/50 px-3 py-2 dark:bg-gray-800/50">
      <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
        {label}
      </span>
      <span className="text-sm font-bold text-gray-900 dark:text-white">
        {value}
      </span>
    </div>
  );
}

function DistributionRow({
  label,
  value,
  tooltip,
}: Readonly<{
  label: string;
  value: string;
  tooltip?: string;
}>) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-white/50 px-3 sm:px-4 py-2 dark:bg-red-900/20">
      <span className="flex items-center gap-1 text-xs sm:text-sm font-bold text-purple-700 dark:text-purple-400">
        {label} %
        {tooltip && (
          <Tooltip content={tooltip}>
            <FiInfo
              size={12}
              className="cursor-help opacity-70 hover:opacity-100"
            />
          </Tooltip>
        )}
      </span>
      <span className="text-sm font-bold text-purple-900 dark:text-purple-300">
        {value}
      </span>
    </div>
  );
}
export default function BodyAvaliationModal({
  isOpen,
  onClose,
  studentName,
  professionalName,
  data,
  calculated,
  date,
  avaliations,
  onSelectAvaliation,
}: BodyAvaliationModalProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    leftSide: true,
    rightSide: true,
    skinfolds: true,
    bioimpedance: true,
    distribution: true,
  });
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Previne scroll do body quando modal está aberto
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen || !data) return null;

  const formatNumber = (value?: number | string | null) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = typeof value === "string" ? Number(value) : value;
    if (Number.isNaN(num)) return "-";
    return num.toFixed(2).replace(".", ",");
  };

  const parseValue = (value?: number | string | null): number | null => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number") return Number.isNaN(value) ? null : value;
    const normalized = String(value).replace(",", ".");
    const num = Number(normalized);
    if (Number.isNaN(num)) return null;
    return num;
  };

  const safeDate = date || data.registe || "";
  const formattedDate = safeDate
    ? new Date(safeDate).toLocaleDateString("pt-BR")
    : "-";

  const pesoTotal = parseValue(data.peso) || 0;
  const pctGordura =
    calculated.percentualGordura ??
    parseValue(data.biopedancia?.percentual_gordura);
  let pesoGordo = calculated.pesoGordo ?? null;
  let pesoMagro = calculated.pesoMagro ?? null;
  if (
    (pesoGordo === null || pesoMagro === null) &&
    pesoTotal &&
    pctGordura !== null
  ) {
    pesoGordo = (pesoTotal * pctGordura) / 100;
    pesoMagro = pesoTotal - pesoGordo;
  }
  let gordKg = pesoGordo ?? 0;
  let magroKg = pesoMagro ?? 0;
  const total = gordKg + magroKg;
  let alturaGordura = total > 0 ? (gordKg / total) * 100 : 0;
  let alturaMagro = total > 0 ? (magroKg / total) * 100 : 0;
  if (alturaGordura === 0 && alturaMagro === 0 && pctGordura !== null) {
    alturaGordura = pctGordura;
    alturaMagro = 100 - pctGordura;
  } else if (alturaGordura === 0 && alturaMagro === 0) {
    alturaGordura = 50;
    alturaMagro = 50;
    if (pesoTotal > 0) {
      gordKg = (pesoTotal * alturaGordura) / 100;
      magroKg = pesoTotal - gordKg;
    }
  }

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      className="fixed inset-0 z-[9999999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm transition-all duration-300"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div
        className={`relative flex flex-col overflow-hidden bg-white dark:bg-boxdark shadow-2xl transition-all duration-300 ${
          isExpanded
            ? "h-screen w-screen rounded-none"
            : "h-[100dvh] sm:h-[95vh] w-full sm:w-[95vw] sm:max-w-7xl rounded-t-3xl sm:rounded-2xl"
        }`}
        style={{ maxWidth: "100vw" }}
      >
        {/* Header Premium - Mobile Optimized */}
        <div className="relative border-b border-stroke bg-gradient-to-r from-purple-50 via-purple-50 to-purple-50 px-4 py-4 sm:px-8 sm:py-6 dark:border-strokedark dark:from-purple-950/30 dark:via-purple-950/30 dark:to-purple-950/30 flex-shrink-0 overflow-x-hidden">
          {/* Decorative Background Pattern */}
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

          {/* Botões de Ação */}
          <div className="absolute right-4 top-4 sm:right-6 sm:top-6 z-10 flex items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="hidden sm:flex rounded-xl p-2 text-gray-500 outline-none transition-all hover:bg-white hover:text-gray-700 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:hover:bg-boxdark dark:hover:text-gray-200 touch-manipulation"
              aria-label={isExpanded ? "Reduzir modal" : "Expandir modal"}
            >
              {isExpanded ? (
                <FiMinimize2 size={20} />
              ) : (
                <FiMaximize2 size={20} />
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl p-2 text-gray-500 outline-none transition-all hover:bg-white hover:text-gray-700 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:hover:bg-boxdark dark:hover:text-gray-200 touch-manipulation active:scale-95"
              aria-label="Fechar avaliação corporal"
            >
              <FiX size={22} />
            </button>
          </div>

          <div className="relative flex items-start gap-3 sm:gap-5 pr-16 sm:pr-24 min-w-0 max-w-full">
            {/* Icon com Ring */}
            <div className="relative flex-shrink-0">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-purple-600 to-purple-600 opacity-75 blur-sm"></div>
              <div className="relative flex h-14 w-14 sm:h-20 sm:w-20 items-center justify-center overflow-hidden rounded-full border-3 sm:border-4 border-white bg-purple-600 shadow-xs dark:border-boxdark">
                <FiActivity className="text-white" size={24} />
              </div>
            </div>

            <div className="flex-1 min-w-0 pt-1">
              <h2
                id="modal-title"
                className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-tight"
              >
                Avaliação Física
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Composição corporal detalhada
              </p>

              {/* Quick Stats - Mobile Optimized */}
              <div className="mt-3 sm:mt-4 flex flex-nowrap overflow-x-auto gap-2 scrollbar-hide -webkit-overflow-scrolling-touch max-w-full">
                <div className="flex items-center gap-1.5 sm:gap-2 rounded-lg bg-white/80 px-2 sm:px-2.5 py-1.5 shadow-sm backdrop-blur-sm dark:bg-gray-800/80 flex-shrink-0">
                  <FiActivity
                    className="text-purple-600 flex-shrink-0"
                    size={14}
                  />
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {studentName}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 rounded-lg bg-white/80 px-2 sm:px-2.5 py-1.5 shadow-sm backdrop-blur-sm dark:bg-gray-800/80 flex-shrink-0">
                  <FiActivity
                    className="text-purple-600 flex-shrink-0"
                    size={14}
                  />
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {professionalName}
                  </span>
                </div>
                {avaliations && avaliations.length > 0 && onSelectAvaliation ? (
                  <div className="flex items-center gap-1.5 sm:gap-2 rounded-lg bg-white/80 px-2 sm:px-2.5 py-1.5 shadow-sm backdrop-blur-sm dark:bg-gray-800/80 flex-shrink-0">
                    <select
                      value={data.id || ""}
                      onChange={(e) => {
                        const selected = avaliations.find(
                          (av) => String(av.id) === e.target.value,
                        );
                        if (selected) onSelectAvaliation(selected);
                      }}
                      className="appearance-none border-none bg-transparent p-0 text-xs font-medium text-gray-700 outline-none focus:ring-0 dark:text-gray-300 touch-manipulation whitespace-nowrap"
                    >
                      {avaliations.map((av) => (
                        <option
                          key={av.id}
                          value={av.id}
                          className="text-gray-900 dark:text-gray-100"
                        >
                          {new Date(
                            av.date || av.registe || av.created_at || "",
                          ).toLocaleDateString("pt-BR")}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 sm:gap-2 rounded-lg bg-white/80 px-2 sm:px-2.5 py-1.5 shadow-sm backdrop-blur-sm dark:bg-gray-800/80 flex-shrink-0">
                    <FiActivity
                      className="text-purple-600 flex-shrink-0"
                      size={14}
                    />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {formattedDate}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo Scrollável */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain bg-gray-50/50 p-4 sm:p-6 md:p-8 dark:bg-gray-900/20 -webkit-overflow-scrolling-touch">
          {/* Informações Adicionais - Mobile First */}
          {(data.motivacao !== undefined ||
            data.sono !== undefined ||
            data.frequencia_treino !== undefined) && (
            <div className="mb-4 sm:mb-6 rounded-2xl border border-stroke bg-gradient-to-br from-indigo-50 to-purple-50 p-4 sm:p-6 shadow-sm dark:border-strokedark dark:from-indigo-950/20 dark:to-purple-950/20">
              <h4 className="mb-3 sm:mb-4 flex items-center gap-2 text-base sm:text-lg font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                <FiTarget
                  className="text-indigo-600 dark:text-indigo-400 flex-shrink-0"
                  size={18}
                />
                <span className="text-sm sm:text-base">
                  Informações Adicionais
                </span>
              </h4>
              <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-3">
                {data.motivacao !== undefined && (
                  <div className="flex flex-col items-center justify-center rounded-lg bg-white/50 px-3 py-3 dark:bg-gray-800/50">
                    <div className="mb-2 flex items-center gap-2">
                      <FiZap className="text-orange-500" size={16} />
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Motivação
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, index) => {
                        const starValue = index + 1;
                        const isFilled = starValue <= (data.motivacao || 0);
                        return (
                          <FiZap
                            key={`motivacao-star-${starValue}`}
                            size={18}
                            className={
                              isFilled
                                ? "fill-orange-400 text-orange-400"
                                : "text-gray-300 dark:text-gray-600"
                            }
                          />
                        );
                      })}
                      <span className="ml-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {data.motivacao || 0}/5
                      </span>
                    </div>
                  </div>
                )}
                {data.sono !== undefined && (
                  <div className="flex flex-col items-center justify-center rounded-lg bg-white/50 px-3 py-3 dark:bg-gray-800/50">
                    <div className="mb-2 flex items-center gap-2">
                      <FiMoon className="text-blue-500" size={16} />
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Qualidade do Sono
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, index) => {
                        const starValue = index + 1;
                        const isFilled = starValue <= (data.sono || 0);
                        return (
                          <FiStar
                            key={`sono-star-${starValue}`}
                            size={18}
                            className={
                              isFilled
                                ? "fill-blue-400 text-blue-400"
                                : "text-gray-300 dark:text-gray-600"
                            }
                          />
                        );
                      })}
                      <span className="ml-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {data.sono || 0}/5
                      </span>
                    </div>
                  </div>
                )}
                {data.frequencia_treino !== undefined && (
                  <div className="flex flex-col items-center justify-center rounded-lg bg-white/50 px-3 py-3 dark:bg-gray-800/50">
                    <div className="mb-2 flex items-center gap-2">
                      <FaDumbbell className="text-green-500" size={16} />
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Frequência de Treino
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 7 }).map((_, index) => {
                        const value = index + 1;
                        const isFilled = value <= (data.frequencia_treino || 0);
                        return (
                          <FaDumbbell
                            key={`frequencia-dumbbell-${value}`}
                            size={16}
                            className={
                              isFilled
                                ? "fill-green-400 text-green-400"
                                : "text-gray-300 dark:text-gray-600"
                            }
                          />
                        );
                      })}
                      <span className="ml-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {data.frequencia_treino || 0}/7
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stats Cards - Grid Responsivo */}
          <div className="mb-4 sm:mb-6 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              icon={FiTrendingUp}
              label="% de Gordura"
              value={formatNumber(calculated.percentualGordura)}
              subtitle="Pollock 7 Dobras"
              color="purple"
              tooltip="Percentual de gordura corporal estimado pelo protocolo de Pollock 7 dobras."
            />
            <StatCard
              icon={FiTarget}
              label="IMC"
              value={formatNumber(calculated.imc)}
              subtitle="Índice de Massa"
              color="blue"
              tooltip="Índice de Massa Corporal. Fórmula: Peso / (Altura × Altura)."
            />
            <StatCard
              icon={FiActivity}
              label="Peso Magro"
              value={formatNumber(calculated.pesoMagro)}
              subtitle="Massa Livre"
              color="green"
              tooltip="Massa livre de gordura (músculos, ossos, órgãos). Fórmula: Peso − (Peso × %G / 100)."
            />
            <StatCard
              icon={FiTarget}
              label="Peso Gordo"
              value={formatNumber(calculated.pesoGordo)}
              subtitle="Tecido Adiposo"
              color="orange"
              tooltip="Quantidade total de gordura corporal. Fórmula: Peso − Peso Magro."
            />
          </div>

          {/* Layout Mobile-First: Stack Vertical */}
          <div className="space-y-4 sm:space-y-6">
            {/* Dados Básicos - Sempre no Topo no Mobile */}
            <div className="lg:hidden rounded-2xl border border-stroke bg-gradient-to-br from-gray-50 to-gray-100 p-4 dark:border-strokedark dark:from-gray-800 dark:to-gray-900">
              <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                Dados Básicos
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <InfoCardMobile
                  label="Peso"
                  value={`${formatNumber(data.peso)} kg`}
                />
                <InfoCardMobile
                  label="Altura"
                  value={`${formatNumber(data.altura)} cm`}
                />
                <InfoCardMobile
                  label="Idade"
                  value={`${formatNumber(data.idade)} anos`}
                />
              </div>
            </div>

            {/* Seções Colapsáveis no Mobile */}
            <CollapsibleSection
              title="Medidas - Lado Esquerdo"
              isExpanded={expandedSections.leftSide}
              onToggle={() => toggleSection("leftSide")}
              color="purple"
            >
              <div className="space-y-1">
                <MeasureRow label="Ombros" value="-" />
                <MeasureRow
                  label="Braço Esquerdo"
                  value={data.braco_esquerdo}
                />
                <MeasureRow
                  label="Braço Esquerdo Contraído"
                  value={data.braco_esquerdo_contraido}
                />
                <MeasureRow label="Cintura" value={data.cintura} />
                <MeasureRow label="Abdômen" value={data.abdomen} />
                <MeasureRow label="Coxa Esquerda" value={data.coxa_esquerda} />
                <MeasureRow
                  label="Perna Esquerda"
                  value={data.perna_esquerda}
                />
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Medidas - Lado Direito"
              isExpanded={expandedSections.rightSide}
              onToggle={() => toggleSection("rightSide")}
              color="blue"
            >
              <div className="space-y-1">
                <MeasureRow label="Braço Direito" value={data.braco_direito} />
                <MeasureRow
                  label="Braço Direito Contraído"
                  value={data.braco_direito_contraido}
                />
                <MeasureRow label="Tórax" value={data.torax} />
                <MeasureRow label="Quadril" value={data.quadril} />
                <MeasureRow label="Coxa Direita" value={data.coxa_direita} />
                <MeasureRow label="Perna Direita" value={data.perna_direita} />
              </div>
            </CollapsibleSection>

            {data.dobra_cutanea && (
              <CollapsibleSection
                title="Dobras Cutâneas (mm)"
                isExpanded={expandedSections.skinfolds}
                onToggle={() => toggleSection("skinfolds")}
                color="purple"
              >
                <div className="space-y-1">
                  <MeasureRow
                    label="Tricipital"
                    value={data.dobra_cutanea.tricipal}
                  />
                  <MeasureRow
                    label="Subescapular"
                    value={data.dobra_cutanea.subescapular}
                  />
                  <MeasureRow
                    label="Peitoral"
                    value={getPeitoralFold(data.dobra_cutanea) ?? "-"}
                  />
                  <MeasureRow
                    label="Bíceps"
                    value={data.dobra_cutanea.biceps}
                  />
                  <MeasureRow
                    label="Supraíliaca"
                    value={data.dobra_cutanea.suprailiaca}
                  />
                  <MeasureRow
                    label="Axilar Média"
                    value={data.dobra_cutanea.axilar_media}
                  />
                  <MeasureRow
                    label="Abdominal"
                    value={data.dobra_cutanea.abdominal}
                  />
                  <MeasureRow label="Coxa" value={data.dobra_cutanea.coxa} />
                  <MeasureRow
                    label="Panturrilha"
                    value={data.dobra_cutanea.panturrilha}
                  />
                </div>
              </CollapsibleSection>
            )}

            <CollapsibleSection
              title="Bioimpedância"
              isExpanded={expandedSections.bioimpedance}
              onToggle={() => toggleSection("bioimpedance")}
              color="green"
              tooltip="Dados obtidos diretamente através da balança de bioimpedância."
            >
              <div className="space-y-2">
                <InfoRow
                  label="% Gordura"
                  value={`${formatNumber(data.biopedancia.percentual_gordura)}%`}
                />
                <InfoRow
                  label="% Músculo"
                  value={`${formatNumber(data.biopedancia.percentual_musculo)}%`}
                />
                <InfoRow
                  label="Idade Metabólica"
                  value={`${formatNumber(data.idade_metabolica)} anos`}
                />
                <InfoRow
                  label="Taxa Metab. Basal"
                  value={`${formatNumber(data.taxa_metabolica)} kcal`}
                />
                <InfoRow
                  label="Gordura Visceral"
                  value={formatNumber(data.indice_de_gordura_visceral)}
                />
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Distribuição de Dobras"
              isExpanded={expandedSections.distribution}
              onToggle={() => toggleSection("distribution")}
              color="red"
            >
              <div className="space-y-3">
                <DistributionRow
                  label="MMSS"
                  value={formatNumber(calculated.distribuicaoMMSS)}
                  tooltip="Distribuição em Membros Superiores. VMP = (Média(Tricipital, Bíceps) × 100) / (Média Geral × 9)."
                />
                <DistributionRow
                  label="MMII"
                  value={formatNumber(calculated.distribuicaoMMII)}
                  tooltip="Distribuição em Membros Inferiores. VMP = (Média(Coxa) × 100) / (Média Geral × 9)."
                />
                <DistributionRow
                  label="Tronco"
                  value={formatNumber(calculated.distribuicaoTronco)}
                  tooltip="Distribuição no Tronco. VMP = (Média(Subescapular, Supra-ilíaca, Peitoral, Abdominal, Axilar Média) × 100) / (Média Geral × 9)."
                />
              </div>
            </CollapsibleSection>

            {/* Somatório de Perímetros */}
            <div className="rounded-2xl border border-stroke bg-gradient-to-br from-blue-50 to-cyan-50 p-4 sm:p-6 dark:border-strokedark dark:from-blue-950/20 dark:to-cyan-950/20">
              <h4 className="mb-3 flex items-center justify-center gap-2 text-center text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                Somatório de Perímetros
                <Tooltip content="Soma de todas as circunferências corporais (ombros, braços, tórax, cintura, abdômen, quadril, coxas e pernas).">
                  <FiInfo
                    size={14}
                    className="cursor-help text-gray-400 hover:text-gray-600"
                  />
                </Tooltip>
              </h4>
              <div className="flex h-20 sm:h-24 items-end justify-center">
                <div
                  className="w-16 sm:w-20 rounded-t-lg bg-gradient-to-t from-blue-500 to-blue-600 shadow-sm"
                  style={{ height: "85%" }}
                ></div>
              </div>
              <p className="mt-3 text-center text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {formatNumber(
                  calculated.somatorioPerimetros ??
                    calculateSomatorioPerimetros(data) ??
                    "-",
                )}
              </p>
            </div>

            {/* Layout Desktop: 3 Colunas (oculto no mobile) */}
            <div className="hidden lg:grid lg:grid-cols-12 gap-6">
              {/* Coluna Esquerda */}
              <div className="lg:col-span-3 space-y-6">
                <div className="rounded-2xl border border-stroke bg-white p-6 shadow-sm dark:border-strokedark dark:bg-meta-4">
                  <h4 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                    <div className="h-1 w-8 rounded bg-purple-600"></div>
                    Lado Esquerdo
                  </h4>
                  <div className="space-y-1">
                    <MeasureRow label="Ombros" value="-" />
                    <MeasureRow
                      label="Braço Esquerdo"
                      value={data.braco_esquerdo}
                    />
                    <MeasureRow
                      label="Braço Esquerdo Contraído"
                      value={data.braco_esquerdo_contraido}
                    />
                    <MeasureRow label="Cintura" value={data.cintura} />
                    <MeasureRow label="Abdômen" value={data.abdomen} />
                    <MeasureRow
                      label="Coxa Esquerda"
                      value={data.coxa_esquerda}
                    />
                    <MeasureRow
                      label="Perna Esquerda"
                      value={data.perna_esquerda}
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-stroke bg-gradient-to-br from-gray-50 to-gray-100 p-6 dark:border-strokedark dark:from-gray-800 dark:to-gray-900">
                  <h4 className="mb-4 text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                    Dados Básicos
                  </h4>
                  <div className="space-y-2">
                    <InfoRow
                      label="Peso"
                      value={`${formatNumber(data.peso)} kg`}
                    />
                    <InfoRow
                      label="Altura"
                      value={`${formatNumber(data.altura)} cm`}
                    />
                    <InfoRow
                      label="Idade"
                      value={`${formatNumber(data.idade)} anos`}
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-stroke bg-gradient-to-br from-gray-50 to-gray-100 p-6 dark:border-strokedark dark:from-gray-800 dark:to-gray-900">
                  <h4 className="mb-4 text-center text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                    Distribuição de Dobras
                  </h4>
                  <div className="space-y-3">
                    <DistributionRow
                      label="MMSS"
                      value={formatNumber(calculated.distribuicaoMMSS)}
                      tooltip="Distribuição em Membros Superiores."
                    />
                    <DistributionRow
                      label="MMII"
                      value={formatNumber(calculated.distribuicaoMMII)}
                      tooltip="Distribuição em Membros Inferiores."
                    />
                    <DistributionRow
                      label="Tronco"
                      value={formatNumber(calculated.distribuicaoTronco)}
                      tooltip="Distribuição no Tronco."
                    />
                  </div>
                </div>
              </div>

              {/* Coluna Central */}
              <div className="lg:col-span-6">
                <div className="h-full rounded-2xl border border-stroke bg-gradient-to-b from-blue-50 to-purple-50 p-8 shadow-sm dark:border-strokedark dark:from-blue-950/20 dark:to-purple-950/20">
                  <div className="flex h-full flex-col items-center justify-center">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 -z-10 animate-pulse rounded-full bg-purple-200 opacity-20 blur-sm dark:bg-purple-800"></div>
                      <img
                        src="/images/3d-rendem-de-uma-figura-masculina-com-um-mapa-detalhado-musculo-2.png"
                        alt="Figura humana com mapa muscular"
                        className="h-96 w-auto max-w-full object-contain drop-shadow-2xl"
                      />
                    </div>

                    <div className="w-full rounded-xl border border-purple-200 bg-white/80 p-6 backdrop-blur-sm dark:border-purple-800 dark:bg-gray-900/80">
                      <h4 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                        <div className="h-1 w-8 rounded bg-green-600"></div>
                        Bioimpedância
                        <Tooltip content="Dados obtidos através da balança de bioimpedância.">
                          <FiInfo size={14} className="text-gray-400" />
                        </Tooltip>
                      </h4>
                      <div>
                        <InfoRow
                          label="% Gordura"
                          value={`${formatNumber(data.biopedancia.percentual_gordura)}%`}
                        />
                        <InfoRow
                          label="% Músculo"
                          value={`${formatNumber(data.biopedancia.percentual_musculo)}%`}
                        />
                        <InfoRow
                          label="Idade Metabólica"
                          value={`${formatNumber(data.idade_metabolica)} anos`}
                        />
                        <InfoRow
                          label="Taxa Metab. Basal"
                          value={`${formatNumber(data.taxa_metabolica)} kcal`}
                        />
                        <InfoRow
                          label="Gordura Visceral"
                          value={formatNumber(data.indice_de_gordura_visceral)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Coluna Direita */}
              <div className="space-y-6 lg:col-span-3">
                <div className="rounded-2xl border border-stroke bg-white p-6 shadow-sm dark:border-strokedark dark:bg-meta-4">
                  <h4 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                    <div className="h-1 w-8 rounded bg-blue-600"></div>
                    Lado Direito
                  </h4>
                  <div className="space-y-1">
                    <MeasureRow
                      label="Braço Direito"
                      value={data.braco_direito}
                    />
                    <MeasureRow
                      label="Braço Direito Contraído"
                      value={data.braco_direito_contraido}
                    />
                    <MeasureRow label="Tórax" value={data.torax} />
                    <MeasureRow label="Quadril" value={data.quadril} />
                    <MeasureRow
                      label="Coxa Direita"
                      value={data.coxa_direita}
                    />
                    <MeasureRow
                      label="Perna Direita"
                      value={data.perna_direita}
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-stroke bg-gradient-to-br from-yellow-50 to-orange-50 p-6 dark:border-strokedark dark:from-yellow-950/20 dark:to-orange-950/20">
                  <h4 className="mb-4 flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                    Composição Corporal
                    <Tooltip content="Representação visual da massa gorda vs massa magra.">
                      <FiInfo size={14} className="cursor-help text-gray-400" />
                    </Tooltip>
                  </h4>
                  <div className="flex h-40 items-end justify-center gap-4 max-h-[140px]">
                    <div className="flex flex-col items-center">
                      <div
                        className="w-16 rounded-t-lg bg-gradient-to-t from-yellow-400 to-yellow-500 shadow-sm transition-all hover:scale-105"
                        style={{ height: `${alturaGordura}px` }}
                      ></div>
                      <p className="mt-1 text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Gordura
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {formatNumber(gordKg)} kg
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div
                        className="w-16 rounded-t-lg bg-gradient-to-t from-green-500 to-green-600 shadow-sm transition-all hover:scale-105"
                        style={{ height: `${alturaMagro}px` }}
                      ></div>
                      <p className="mt-1 text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Magro
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {formatNumber(magroKg)} kg
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-stroke bg-gradient-to-br from-blue-50 to-cyan-50 p-6 dark:border-strokedark dark:from-blue-950/20 dark:to-cyan-950/20">
                  <h4 className="mb-4 flex items-center justify-center gap-2 text-center text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                    Somatório de Perímetros
                    <Tooltip content="Soma de todas as circunferências corporais.">
                      <FiInfo size={14} className="cursor-help text-gray-400" />
                    </Tooltip>
                  </h4>
                  <div className="flex h-24 items-end justify-center">
                    <div
                      className="w-20 rounded-t-lg bg-gradient-to-t from-blue-500 to-blue-600 shadow-sm"
                      style={{ height: "85%" }}
                    ></div>
                  </div>
                  <p className="mt-3 text-center text-2xl font-bold text-gray-900 dark:text-white">
                    {formatNumber(
                      calculated.somatorioPerimetros ??
                        calculateSomatorioPerimetros(data) ??
                        "-",
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Galeria de Imagens */}
          {data.images && data.images.length > 0 && (
            <div className="mt-6 sm:mt-8 rounded-2xl border border-stroke bg-white p-4 sm:p-6 shadow-sm dark:border-strokedark dark:bg-boxdark">
              <h4 className="mb-4 sm:mb-6 flex items-center gap-2 text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                <div className="h-5 sm:h-6 w-1 rounded-full bg-primary"></div>
                Galeria de Imagens
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
                {data.images.map((img, index) => (
                  <button
                    type="button"
                    key={`${img}-${index}`}
                    onClick={() => {
                      setSelectedImageIndex(index);
                      setViewerOpen(true);
                    }}
                    className="group relative aspect-square overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800 touch-manipulation cursor-pointer w-full p-0 border-0"
                  >
                    <img
                      src={img}
                      alt={`Avaliação ${index + 1}`}
                      className="h-full w-full object-cover transition-transform duration-300 group-active:scale-110 group-hover:scale-105"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <ImageViewerModal
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        images={data.images || []}
        initialIndex={selectedImageIndex}
      />
    </div>,
    document.body,
  );
}
